import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, Ticket, ArrowRight, ArrowLeft, Lock, ShieldAlert,
  UserPlus, GitBranch, Send, Search, Clock,
} from 'lucide-react';
import AuthShell from '../../components/auth/AuthShell';
import {
  AuthHeading, AuthField, AuthInput, AuthSelect, AuthTextarea, ErrorBanner, PrimaryButton,
} from '../../components/auth/AuthPrimitives';
import organizationsApi from '../../utils/organizationsApi';

// OrganizationPage — Step 2 of signup, ported from the prototype
// docs/design/auth-subscription-prototype/screens/OrganizationScreen.jsx.
//
// Three paths from a chooser: create | join | invite.
//
// DEDUP (warn-never-merge, NO PHI disclosure): creating an org runs the server's
// EIN/TIN dedup gate. An exact tax-ID match does NOT auto-merge and NEVER reveals
// the existing org's identity (name / members / address). The verbatim server
// `message` is shown, and the user chooses: request access, enter an invite code,
// or create a separate organization (with a required reason routed to manual
// review). The matched org's name is never fetched or rendered — the API does not
// return it, so this UI cannot leak it.
//
// Endpoint wiring (08-04 / 08-05):
//  - create ............... POST /organizations/          (dedup-gated)
//  - dedup "request access" POST /organizations/          (create_separate + auto reason -> pending review row)
//  - dedup "separate org" . POST /organizations/          (create_separate + user reason -> pending review row)
//  - invite code .......... POST /organizations/invitations/accept/
//  - join (by org id) ..... POST /organizations/join-requests/

const APP_HOME = '/overview';

const ORG_TYPES = [
  { value: 'practice', label: 'Practice' },
  { value: 'billing_company', label: 'Billing Company' },
  { value: 'health_system', label: 'Health System' },
  { value: 'other', label: 'Other' },
];

// Normalize an EIN to digits only, matching the backend's re.sub(r"\D", "", ...).
function normalizeEin(raw) { return (raw || '').replace(/\D/g, ''); }
function formatEin(raw) {
  const d = normalizeEin(raw).slice(0, 9);
  return d.length > 2 ? `${d.slice(0, 2)}-${d.slice(2)}` : d;
}

export default function OrganizationPage({ onLogin }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState('choose'); // choose | create | join | invite

  return (
    <AuthShell onLogoClick={() => navigate('/login')} wide>
      {mode === 'choose' && <ChoosePath onPick={setMode} />}
      {mode === 'create' && <CreateOrg navigate={navigate} onLogin={onLogin} back={() => setMode('choose')} />}
      {mode === 'join' && <JoinOrg back={() => setMode('choose')} />}
      {mode === 'invite' && <InviteOrg navigate={navigate} onLogin={onLogin} back={() => setMode('choose')} />}
    </AuthShell>
  );
}

/* ------------------------------------------------------------------ choose */
function ChoosePath({ onPick }) {
  return (
    <div>
      <AuthHeading
        eyebrow="Step 2 of 2 · Organization"
        title="Set up your organization"
        subtitle="Meduri organizes appeals, billing, and access by organization. Create one, join an existing one, or accept an invitation."
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <OrgChoiceCard icon={Building2} title="Create an organization"
          desc="Register your practice, billing company, or health system. You’ll be the owner."
          onClick={() => onPick('create')} />
        <OrgChoiceCard icon={Users} title="Join an existing organization"
          desc="Have an organization ID from an administrator? Request access to it."
          onClick={() => onPick('join')} />
        <OrgChoiceCard icon={Ticket} title="Accept an invitation"
          desc="Have an invite code from a colleague? Enter it to join instantly."
          onClick={() => onPick('invite')} />
      </div>
    </div>
  );
}

function OrgChoiceCard({ icon: Icon, title, desc, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button type="button" onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        textAlign: 'left', width: '100%', cursor: 'pointer', font: 'inherit',
        display: 'flex', alignItems: 'flex-start', gap: 16,
        border: `1px solid ${hover ? '#000' : '#D5DAE1'}`, background: hover ? '#F8F9FC' : '#fff',
        padding: 20, transition: 'border .1s, background .1s',
      }}>
      <span style={{
        width: 40, height: 40, flexShrink: 0, background: '#000', color: '#fff',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}><Icon size={20} /></span>
      <span style={{ flex: 1 }}>
        <span style={{ display: 'block', fontSize: 16, fontWeight: 600, color: '#000' }}>{title}</span>
        <span style={{ display: 'block', fontSize: 13, lineHeight: 1.5, color: '#64748B', marginTop: 4 }}>{desc}</span>
      </span>
      <span style={{ color: '#64748B', display: 'inline-flex', marginTop: 8 }}><ArrowRight size={18} /></span>
    </button>
  );
}

/* ------------------------------------------------------------------ create */
function CreateOrg({ navigate, onLogin, back }) {
  const [data, setData] = useState({ displayName: '', legalName: '', orgType: '', ein: '', npi: '', phone: '', address: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState('');
  const [earlyWarn, setEarlyWarn] = useState('');
  const [dedup, setDedup] = useState(null); // { message } | null
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Effect-safety (CLAUDE.md): the advisory EIN probe is keyed by the STABLE
  // normalized EIN only, and the latest result handler is held in a ref — so a
  // rerender with new parent callbacks (onLogin/navigate) never retriggers it.
  const normEin = normalizeEin(data.ein);
  const probeReady = normEin.length === 9;
  const handleProbe = (result) => setEarlyWarn(result?.duplicate_warning ? (result.message || '') : '');
  const handleProbeRef = useRef(handleProbe);
  useEffect(() => { handleProbeRef.current = handleProbe; });
  useEffect(() => {
    if (!probeReady) { setEarlyWarn(''); return undefined; }
    let cancelled = false;
    organizationsApi.einProbe(normEin)
      .then((r) => { if (!cancelled) handleProbeRef.current(r); })
      .catch(() => { /* advisory only — the authoritative gate is on submit */ });
    return () => { cancelled = true; };
  }, [normEin, probeReady]);

  function validate() {
    const e = {};
    if (!data.displayName.trim()) e.displayName = 'Enter a display name';
    if (!data.legalName.trim()) e.legalName = 'Enter the legal entity name';
    if (!data.orgType) e.orgType = 'Select an organization type';
    if (!normEin) e.ein = 'Enter your EIN / tax ID';
    else if (normEin.length !== 9) e.ein = 'EIN must be 9 digits';
    if (data.npi && !/^\d{10}$/.test(data.npi.trim())) e.npi = 'NPI must be 10 digits';
    if (data.phone && data.phone.replace(/\D/g, '').length !== 10) e.phone = 'Phone must be 10 digits';
    if (!data.address.trim()) e.address = 'Enter a billing or HQ address';
    return e;
  }

  function buildPayload(extra = {}) {
    const payload = {
      name: data.displayName.trim(),
      legal_name: data.legalName.trim(),
      organization_type: data.orgType,
      ein: normEin,
      address: data.address.trim(),
      ...extra,
    };
    if (data.npi.trim()) payload.organizational_npi = data.npi.trim();
    if (data.phone.trim()) payload.contact_phone = data.phone.replace(/\D/g, '');
    return payload;
  }

  function finishActive() {
    onLogin?.();
    // After a clean org create, continue into the subscription step (08-07).
    // Join/invite/reviewed-separate paths still land on APP_HOME below.
    navigate('/plans');
  }

  async function submit(ev) {
    ev?.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setLoading(true);
    setBanner('');
    try {
      const res = await organizationsApi.createOrganization(buildPayload());
      if (res?.duplicate_warning) { setDedup({ message: res.message || '' }); return; }
      finishActive();
    } catch (err) {
      setBanner(err.response?.data?.error || err.response?.data?.detail || 'Could not create the organization. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (dedup) {
    return (
      <DedupResolution
        einDisplay={formatEin(data.ein)}
        message={dedup.message}
        buildPayload={buildPayload}
        navigate={navigate}
        onLogin={onLogin}
        back={() => setDedup(null)}
      />
    );
  }

  const twoCol = true;
  return (
    <form onSubmit={submit} noValidate>
      <AuthHeading
        eyebrow="Step 2 of 2 · Organization"
        title="Create your organization"
        subtitle="These details verify your practice with payers and clearinghouses. Your tax ID is encrypted at rest."
      />
      <ErrorBanner>{banner}</ErrorBanner>
      <div style={{ display: 'grid', gridTemplateColumns: twoCol ? '1fr 1fr' : '1fr', gap: 16, columnGap: 20 }}>
        <AuthField label="Display name" htmlFor="org-display" error={errors.displayName}>
          <AuthInput id="org-display" placeholder="Greenway PT" value={data.displayName}
            onChange={(e) => set('displayName', e.target.value)} error={errors.displayName} />
        </AuthField>
        <AuthField label="Legal name" htmlFor="org-legal" error={errors.legalName}>
          <AuthInput id="org-legal" placeholder="Greenway Physical Therapy LLC" value={data.legalName}
            onChange={(e) => set('legalName', e.target.value)} error={errors.legalName} />
        </AuthField>
        <AuthField label="Organization type" htmlFor="org-type" error={errors.orgType}>
          <AuthSelect id="org-type" placeholder="Select a type…" value={data.orgType}
            onChange={(e) => set('orgType', e.target.value)} error={errors.orgType}>
            {ORG_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </AuthSelect>
        </AuthField>
        <AuthField label="EIN / Tax ID" htmlFor="org-ein" error={errors.ein}
          hint={!errors.ein ? 'Encrypted at rest.' : null}>
          <AuthInput id="org-ein" inputMode="numeric" placeholder="12-3456789" leadingIcon={Lock}
            value={data.ein} onChange={(e) => set('ein', formatEin(e.target.value))} error={errors.ein} />
        </AuthField>
        <AuthField label="Organizational NPI" htmlFor="org-npi" optional error={errors.npi}
          hint={!errors.npi ? '10-digit type-2 NPI, if you have one.' : null}>
          <AuthInput id="org-npi" inputMode="numeric" placeholder="1234567890" maxLength={10}
            value={data.npi} onChange={(e) => set('npi', e.target.value.replace(/\D/g, ''))} error={errors.npi} />
        </AuthField>
        <AuthField label="Billing contact phone" htmlFor="org-phone" optional error={errors.phone}
          hint={!errors.phone ? 'Reached on claim submissions. Required before you can file claims.' : null}>
          <AuthInput id="org-phone" inputMode="tel" placeholder="5550104499" maxLength={14}
            value={data.phone} onChange={(e) => set('phone', e.target.value.replace(/[^\d\s().-]/g, ''))} error={errors.phone} />
        </AuthField>
        <div style={{ gridColumn: twoCol ? '1 / -1' : 'auto' }}>
          <AuthField label="Billing / headquarters address" htmlFor="org-addr" error={errors.address}>
            <AuthTextarea id="org-addr" placeholder={'500 Clinic Way, Suite 200\nBrooklyn, NY 11201'}
              value={data.address} onChange={(e) => set('address', e.target.value)} error={errors.address} />
          </AuthField>
        </div>
      </div>

      {earlyWarn && (
        <div style={{ marginTop: 16 }}>
          <div style={{ border: '1px solid #000', background: '#F8F9FC', padding: 12, display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.5, color: '#1D2433' }}>
            <span style={{ color: '#B7791F', flexShrink: 0, marginTop: 1 }}><ShieldAlert size={16} /></span>
            <span>{earlyWarn}</span>
          </div>
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        <PrimaryButton type="submit" loading={loading} icon={ArrowRight}>Create Organization</PrimaryButton>
      </div>
      <BackRow onClick={back} label="Back to options" />
    </form>
  );
}

/* ----------------------------------------------------- DEDUP resolution UI */
// Warn-never-merge, NO PHI disclosure. Renders only the verbatim server message
// and the user's own EIN input; never the matched organization's identity.
const ACCESS_REQUEST_REASON =
  'Requesting access to the organization already registered under this EIN. Please review and add me as a member.';

function DedupResolution({ einDisplay, message, buildPayload, navigate, onLogin, back }) {
  const [choice, setChoice] = useState(null); // 'request' | 'invite' | 'separate'
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [reasonErr, setReasonErr] = useState(false);
  const [done, setDone] = useState(null); // 'requested' | 'reviewing'
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState('');

  if (done === 'requested') {
    return <PendingState navigate={navigate}
      title="Access request sent"
      body="An administrator of the existing organization will review your request. You’ll get an email when it’s approved." />;
  }
  if (done === 'reviewing') {
    return <PendingState navigate={navigate}
      title="Submitted for review"
      body="Because this tax ID is already registered, our team will verify your separate-organization request before it goes live. This usually takes one business day." />;
  }

  async function act() {
    setBanner('');
    if (choice === 'invite') {
      if (!code.trim()) { setBanner('Enter your invitation code.'); return; }
      setLoading(true);
      try {
        await organizationsApi.acceptInvitation(code.trim());
        onLogin?.();
        navigate(APP_HOME);
      } catch (err) {
        setBanner(err.response?.data?.error || err.response?.data?.detail || 'That code isn’t valid or has expired. Ask your admin for a new one.');
      } finally { setLoading(false); }
      return;
    }
    if (choice === 'separate') {
      if (!reason.trim()) { setReasonErr(true); return; }
      setLoading(true);
      try {
        await organizationsApi.createOrganization(buildPayload({ create_separate: true, reason: reason.trim() }));
        setDone('reviewing');
      } catch (err) {
        setBanner(err.response?.data?.error || err.response?.data?.detail || 'Could not submit for review. Please try again.');
      } finally { setLoading(false); }
      return;
    }
    // request access — routed to manual review (creates a pending review row
    // server-side WITHOUT disclosing the matched org).
    setLoading(true);
    try {
      await organizationsApi.createOrganization(buildPayload({ create_separate: true, reason: ACCESS_REQUEST_REASON }));
      setDone('requested');
    } catch (err) {
      setBanner(err.response?.data?.error || err.response?.data?.detail || 'Could not send the access request. Please try again.');
    } finally { setLoading(false); }
  }

  return (
    <div>
      <AuthHeading
        eyebrow="Step 2 of 2 · Organization"
        title="This tax ID may already be registered"
        subtitle="We found an organization using this EIN. To protect member data we can’t show its details until you’re authorized."
      />
      <ErrorBanner>{banner}</ErrorBanner>
      <div style={{ border: '1px solid #000', background: '#F8F9FC', padding: 16, display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 24 }}>
        <span style={{ color: '#B7791F', flexShrink: 0, marginTop: 1 }}><ShieldAlert size={18} /></span>
        <div style={{ fontSize: 13, lineHeight: 1.5, color: '#1D2433' }}>
          {/* Verbatim server warning message — the only dedup copy shown. */}
          <div>{message}</div>
          <div style={{ marginTop: 8 }}>
            <strong>{einDisplay}</strong> matches an existing organization. Nothing has been created yet — choose how you’d like to proceed.
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <ResolveOption active={choice === 'request'} onClick={() => setChoice('request')}
          icon={UserPlus} title="Request access"
          desc="Ask an admin of the existing organization to add you. Recommended." />
        <ResolveOption active={choice === 'invite'} onClick={() => setChoice('invite')}
          icon={Ticket} title="I have an invite code"
          desc="Join instantly with a code from a colleague." />
        {choice === 'invite' && (
          <div style={{ padding: '4px 2px 8px' }}>
            <AuthInput id="dedup-code" placeholder="e.g. MEDURI-4F2A" value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())} />
          </div>
        )}
        <ResolveOption active={choice === 'separate'} onClick={() => setChoice('separate')}
          icon={GitBranch} title="Create a separate organization"
          desc="Different location, subsidiary, or contractual boundary. Requires a reason and manual review." />
        {choice === 'separate' && (
          <div style={{ padding: '4px 2px 8px' }}>
            <AuthField label="Reason for a separate organization" htmlFor="dedup-reason"
              error={reasonErr ? 'A reason is required for review' : null}>
              <AuthTextarea id="dedup-reason" rows={3}
                placeholder="e.g. Separate Manhattan location with isolated patient data."
                value={reason} onChange={(e) => { setReason(e.target.value); setReasonErr(false); }}
                error={reasonErr} />
            </AuthField>
          </div>
        )}
      </div>

      <div style={{ marginTop: 24 }}>
        <PrimaryButton type="button" loading={loading} disabled={!choice}
          icon={choice === 'invite' ? ArrowRight : Send} onClick={act}>
          {choice === 'invite' ? 'Join & Continue'
            : choice === 'separate' ? 'Submit for Review'
              : 'Send Access Request'}
        </PrimaryButton>
      </div>
      <BackRow onClick={back} label="Back to organization details" />
    </div>
  );
}

function ResolveOption({ active, onClick, icon: Icon, title, desc }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        textAlign: 'left', width: '100%', cursor: 'pointer', font: 'inherit',
        display: 'flex', alignItems: 'flex-start', gap: 12,
        border: `${active ? 2 : 1}px solid ${active ? '#000' : '#D5DAE1'}`,
        background: active ? '#F8F9FC' : '#fff', padding: active ? '13px' : '14px',
        transition: 'border .1s, background .1s',
      }}>
      <span style={{
        width: 16, height: 16, flexShrink: 0, marginTop: 2, borderRadius: '50%',
        border: `1px solid ${active ? '#000' : '#A0ABBB'}`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>{active && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#000' }} />}</span>
      <span style={{ color: '#000', display: 'inline-flex', marginTop: 1 }}><Icon size={16} /></span>
      <span style={{ flex: 1 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#000' }}>{title}</span>
        <span style={{ display: 'block', fontSize: 12.5, lineHeight: 1.45, color: '#64748B', marginTop: 2 }}>{desc}</span>
      </span>
    </button>
  );
}

/* -------------------------------------------------------------------- join */
// No disclosing search endpoint exists, so Join posts a join-request against an
// organization ID the user already holds (shared out-of-band by an admin).
function JoinOrg({ back }) {
  const navigate = useNavigate();
  const [orgId, setOrgId] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(ev) {
    ev?.preventDefault();
    if (!orgId.trim()) { setErr('Enter the organization ID your administrator gave you.'); return; }
    setLoading(true);
    setErr(null);
    try {
      await organizationsApi.createJoinRequest({ organizationId: orgId.trim(), reason: reason.trim() || undefined });
      setSent(true);
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) setErr('We couldn’t find an organization with that ID. Check it with your administrator.');
      else setErr(e.response?.data?.error || e.response?.data?.detail || 'Could not send the request. Please try again.');
    } finally { setLoading(false); }
  }

  if (sent) {
    return <PendingState navigate={navigate}
      title="Access request sent"
      body="An administrator will review your request to join. You’ll get an email once you’re approved." />;
  }

  return (
    <form onSubmit={submit} noValidate>
      <AuthHeading
        eyebrow="Step 2 of 2 · Organization"
        title="Join an organization"
        subtitle="Access is granted by an administrator. Enter the organization ID they shared with you — we never expose member data before approval."
      />
      {err && <div style={{ marginBottom: 20 }}><ErrorBanner>{err}</ErrorBanner></div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <AuthField label="Organization ID" htmlFor="join-id">
          <AuthInput id="join-id" placeholder="00000000-0000-0000-0000-000000000000" leadingIcon={Search}
            value={orgId} onChange={(e) => { setOrgId(e.target.value); setErr(null); }} />
        </AuthField>
        <AuthField label="Note to the administrator" htmlFor="join-reason" optional>
          <AuthTextarea id="join-reason" rows={3} placeholder="e.g. I’m the new billing specialist at the front desk."
            value={reason} onChange={(e) => setReason(e.target.value)} />
        </AuthField>
      </div>
      <div style={{ marginTop: 24 }}>
        <PrimaryButton type="submit" loading={loading} icon={UserPlus}>Request Access</PrimaryButton>
      </div>
      <BackRow onClick={back} label="Back to options" />
    </form>
  );
}

/* ------------------------------------------------------------------ invite */
function InviteOrg({ navigate, onLogin, back }) {
  const [code, setCode] = useState('');
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  async function submit(ev) {
    ev?.preventDefault();
    if (!code.trim()) { setErr('Enter your invitation code'); return; }
    setLoading(true);
    setErr(null);
    try {
      await organizationsApi.acceptInvitation(code.trim());
      onLogin?.();
      navigate(APP_HOME);
    } catch (e) {
      setErr(e.response?.data?.error || e.response?.data?.detail || 'That code isn’t valid or has expired. Ask your admin for a new one.');
    } finally { setLoading(false); }
  }

  return (
    <form onSubmit={submit} noValidate>
      <AuthHeading
        eyebrow="Step 2 of 2 · Organization"
        title="Enter your invitation code"
        subtitle="Your colleague sent this from Meduri. You’ll join their organization with the role they assigned."
      />
      {err && <div style={{ marginBottom: 20 }}><ErrorBanner>{err}</ErrorBanner></div>}
      <AuthField label="Invitation code" htmlFor="inv-code">
        <AuthInput id="inv-code" placeholder="MEDURI-4F2A" leadingIcon={Ticket}
          value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setErr(null); }} />
      </AuthField>
      <div style={{ marginTop: 24 }}>
        <PrimaryButton type="submit" loading={loading} icon={ArrowRight}>Join Organization</PrimaryButton>
      </div>
      <BackRow onClick={back} label="Back to options" />
    </form>
  );
}

/* --------------------------------------------------------------- shared UI */
function PendingState({ navigate, title, body }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: 56, height: 56, margin: '0 auto 24px', border: '1px solid #000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Clock size={26} style={{ color: '#000' }} />
      </div>
      <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0, color: '#000', textWrap: 'balance' }}>{title}</h1>
      <p style={{ fontSize: 15, lineHeight: 1.55, color: '#64748B', margin: '12px auto 0', maxWidth: 420, textWrap: 'pretty' }}>{body}</p>
      <div style={{ marginTop: 28, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button type="button" onClick={() => navigate('/login')}
          style={{ fontSize: 14, fontWeight: 600, background: '#fff', color: '#000', border: '1px solid #000', padding: '13px 20px', cursor: 'pointer', fontFamily: 'inherit' }}>
          Back to sign in
        </button>
      </div>
    </div>
  );
}

function BackRow({ onClick, label }) {
  return (
    <div style={{ marginTop: 20, paddingTop: 20, borderTop: '0.4px solid #000', textAlign: 'center' }}>
      <a onClick={onClick} style={{ fontSize: 14, color: '#000', textDecoration: 'underline', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <ArrowLeft size={14} /> {label}
      </a>
    </div>
  );
}
